import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Percent, Wallet, Trophy, TrendingDown, Flame, Upload, ChefHat, Database, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader, EmptyState, SeverityBadge } from "@/components/Logo";
import { MarginEvolutionChart, InflationChart, DishesChart } from "@/components/dashboard/Charts";
import Recommendations from "@/components/dashboard/Recommendations";
import { track } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { eur, pct, foodCostTone, dateFr } from "@/lib/format";
import { t } from "@/i18n";

function Kpi({ icon: Icon, label, value, sub, tone = "", testId, delay = 0, className = "" }) {
  return (
    <div className={`card-lift min-w-0 rounded-xl border bg-card p-4 fade-up sm:p-5 ${className}`} style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <p className="eyebrow line-clamp-2 text-[10px] sm:text-xs">{label}</p>
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
      <p className={`mt-2 line-clamp-2 text-lg font-bold leading-tight tracking-tight sm:mt-3 sm:text-xl ${tone}`} title={typeof value === "string" ? value : undefined} data-testid={`${testId}-value`}>{value}</p>
      <p className="mt-1 line-clamp-2 text-[11px] text-muted-foreground sm:text-xs" title={sub}>{sub}</p>
    </div>
  );
}

const NoDishes = () => <p className="py-10 text-center text-sm text-muted-foreground" data-testid="dishes-chart-empty">{t("dashboard.ajoutez_les_ingredients_de_vos_plats_pour")}</p>;

function Panel({ title, subtitle, children, className = "", testId }) {
  return (
    <div className={`min-w-0 rounded-xl border bg-card p-4 sm:p-5 ${className}`} data-testid={testId}>
      <h3 className="text-sm font-semibold">{title}</h3>
      {subtitle && <p className="mb-4 text-xs text-muted-foreground">{subtitle}</p>}
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = () => api.get("/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  const viewed = useRef(false);
  useEffect(() => {
    load();
    if (!viewed.current) { viewed.current = true; track("dashboard_viewed"); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const seed = async () => {
    setSeeding(true);
    try {
      await api.post("/demo/seed");
      toast.success(t("dashboard.donnees_de_demonstration_chargees"));
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSeeding(false);
    }
  };

  if (!d) return <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>;

  const k = d.kpis;
  const costed = k.costed_count > 0;
  const header = (
    <PageHeader
      eyebrow={user?.restaurant?.name}
      title={t("dashboard.bonjour_x", { v0: user?.name?.split(" ")[0] || "" })}
      description={t("dashboard.votre_menu_plat_par_plat_cout_matiere_marg")}
      actions={<>
        <Button variant="outline" asChild data-testid="dashboard-new-recipe-btn"><Link to="/recipes/new"><ChefHat className="mr-2 h-4 w-4 shrink-0" /><span className="truncate"><span className="hidden sm:inline">{t("common.nouvelle")} </span>{t("common.recette")}</span></Link></Button>
        <Button asChild data-testid="dashboard-upload-invoice-btn"><Link to="/invoices"><Upload className="mr-2 h-4 w-4 shrink-0" /><span className="truncate">{t("dashboard.importer")}<span className="hidden sm:inline"> {t("dashboard.une_facture")}</span></span></Link></Button>
      </>}
    />
  );

  if (!d.has_data) {
    return (
      <>
        {header}
        <EmptyState icon={Database} testId="dashboard-empty-state" title={t("dashboard.commencez_par_importer_une_facture_fournis")}
          description={t("dashboard.l_ia_extrait_automatiquement_produits_quan")}>
          <Button asChild data-testid="empty-analyze-menu-btn"><Link to="/ma-carte">{t("dashboard.analyser_ma_carte")}</Link></Button>
          <Button variant="outline" asChild data-testid="empty-upload-btn"><Link to="/invoices">{t("dashboard.importer_une_facture")}</Link></Button>
          <Button variant="outline" onClick={seed} disabled={seeding} data-testid="load-demo-data-btn">
            {seeding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("dashboard.charger_des_donnees_de_demo")}
          </Button>
        </EmptyState>
      </>
    );
  }

  return (
    <>
      {header}
      {k.incomplete_recipes > 0 && (
        <Link to="/recipes?fill=1" className="mb-4 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm transition-colors hover:border-amber-500/60" data-testid="dashboard-incomplete-banner">
          <ChefHat className="h-5 w-5 shrink-0 text-amber-500" />
          <span className="min-w-0 flex-1"><span className="font-semibold">{k.incomplete_recipes} {t("dashboard.plat_s_attendent_leurs_ingredients")}</span> <span className="text-muted-foreground">{t("dashboard.l_ia_peut_les_completer_en_1_clic_pour_cal")}</span></span>
          <span className="hidden shrink-0 font-semibold text-amber-600 sm:inline dark:text-amber-400">{t("dashboard.completer_avec_l_ia")}</span>
        </Link>
      )}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <Kpi icon={Percent} label={t("dashboard.cout_matiere_moyen")} testId="dashboard-kpi-food-cost" value={costed ? pct(k.avg_food_cost_pct) : "—"} tone={costed ? foodCostTone(k.avg_food_cost_pct, d.target_food_cost) : ""} sub={costed ? t("dashboard.objectif_x_x_au_dessus", { v0: d.target_food_cost, v1: k.recipes_above_target }) : t("dashboard.ajoutez_les_ingredients")} />
        <Kpi icon={Wallet} label={t("dashboard.marge_brute_moyenne")} testId="dashboard-kpi-gross-margin" value={costed ? eur(k.avg_margin) : "—"} sub={costed ? t("dashboard.x_du_prix_de_vente", { v0: pct(k.avg_margin_pct) }) : t("dashboard.ajoutez_les_ingredients")} delay={60} />
        <Kpi icon={Trophy} label={t("dashboard.plat_le_plus_rentable")} testId="dashboard-kpi-most-profitable" value={k.most_profitable?.name || "—"} sub={k.most_profitable ? t("dashboard.x_de_marge_x_cm", { v0: eur(k.most_profitable.gross_margin), v1: pct(k.most_profitable.food_cost_pct) }) : t("common.aucune_recette")} delay={120} />
        <Kpi icon={TrendingDown} label={t("dashboard.plat_le_moins_rentable")} testId="dashboard-kpi-least-profitable" value={k.least_profitable?.name || "—"} sub={k.least_profitable ? t("dashboard.x_de_marge_x_cm", { v0: eur(k.least_profitable.gross_margin), v1: pct(k.least_profitable.food_cost_pct) }) : t("common.aucune_recette")} delay={180} />
        <Kpi icon={Flame} label={t("dashboard.plus_forte_hausse")} testId="dashboard-kpi-top-increase" value={k.top_increase?.name || "—"} tone={k.top_increase ? "" : ""} sub={k.top_increase ? t("dashboard.x_x_recette_s", { v0: pct(k.top_increase.change_pct, true), v1: k.top_increase.recipes_count }) : t("dashboard.aucune_hausse_detectee")} delay={240} className="col-span-2 lg:col-span-1" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel className="flex flex-col lg:col-span-2" title={t("dashboard.evolution_de_la_marge")} subtitle={t("dashboard.marge_brute_et_cout_matiere_moyens_du_menu")} testId="dashboard-chart-margin-evolution">
          {costed ? <MarginEvolutionChart data={d.margin_evolution} /> : <NoDishes />}
        </Panel>
        <Recommendations hasRecipes={costed} incomplete={!costed && k.recipes_count > 0} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel title={t("dashboard.inflation_des_ingredients")} subtitle={t("dashboard.variation_depuis_le_premier_prix_enregistr")} testId="dashboard-chart-inflation">
          {d.top_increases.length ? <InflationChart data={d.top_increases} /> : <p className="text-sm text-muted-foreground">{t("dashboard.pas_encore_de_hausse_detectee")}</p>}
        </Panel>
        <Panel title={t("dashboard.top_plats_rentables")} subtitle={t("dashboard.marge_brute_par_plat")} testId="dashboard-chart-top-dishes">
          {d.top_dishes.length ? <DishesChart data={d.top_dishes} color="#10B981" /> : <NoDishes />}
        </Panel>
        <Panel title={t("dashboard.plats_les_moins_rentables")} subtitle={t("dashboard.marge_brute_par_plat")} testId="dashboard-chart-low-dishes">
          {d.low_dishes.length ? <DishesChart data={d.low_dishes} color="#F43F5E" /> : <NoDishes />}
        </Panel>
      </div>

      <Panel className="mt-4" title={t("dashboard.dernieres_alertes_prix")} testId="dashboard-recent-alerts">
        <div className="mt-3 divide-y">
          {d.recent_alerts.length === 0 && <p className="py-3 text-sm text-muted-foreground">{t("dashboard.aucune_alerte_pour_le_moment")}</p>}
          {d.recent_alerts.map((a) => (
            <div key={a.id} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:gap-4">
              <SeverityBadge severity={a.severity}>{pct(a.change_pct, true)}</SeverityBadge>
              <p className="flex-1 text-sm">{a.message}</p>
              <span className="text-xs text-muted-foreground">{dateFr(a.created_at)}</span>
            </div>
          ))}
        </div>
        <Button variant="link" asChild className="px-0" data-testid="dashboard-see-alerts-link"><Link to="/alerts">{t("dashboard.voir_toutes_les_alertes")}</Link></Button>
      </Panel>
    </>
  );
}
